import {ProductVariantsFormValues} from "../../../components/offering-form/products/variants/types";
import {AddOn, SaveAddOnsInput} from "../../../features/digital-products";

/**
 * Shared mapping between the add-ons (variants) form values and the domain
 * add-on shapes. Used by both the edit page and the create wizard so the two
 * flows stay consistent. Pure functions — no React.
 */

/**
 * Build the form's values from loaded/persisted add-ons. Only manually-added
 * currencies are shown (auto-converted rows are backend-generated). Uses each
 * option's own id for `variant_option_id` (the v2 upsert/delete key).
 */
export function addOnsToFormValues(
  addOns: AddOn[],
  showVariants: boolean
): ProductVariantsFormValues {
  return {
    showVariants,
    variants: addOns.map((addOn) => ({
      variant_id: addOn.id,
      name: addOn.name,
      options: addOn.options.map((option) => {
        const manualCurrencies = option.currencies.filter(
          (currency) => currency.isManuallyAdded !== false
        );
        // A free option carries no priced currency: either no rows, or every
        // amount is 0 (v2 encodes free as zero-amount rows, not an empty array).
        const hasPaidAmount = manualCurrencies.some((currency) => currency.amount > 0);
        return {
          variant_option_id: option.id,
          name: option.name,
          priceType: hasPaidAmount ? "paid" : "free",
          currencies: manualCurrencies.map((currency) => ({
            currency: currency.currency,
            // The form schema requires price >= 1 for every row. A free option's
            // amount is 0 on the wire, but its price input is hidden in the UI
            // and forced back to 0 on save (formValuesToSaveInput), so seed a
            // valid placeholder here to keep the re-hydrated form valid.
            price: hasPaidAmount ? currency.amount : 1,
          })),
        };
      }),
    })),
  };
}

/**
 * Map the submitted form back to the save input. Free options → empty
 * currencies; form-entered currencies are marked manual (the backend
 * regenerates conversions). Ids are carried through for upsert.
 */
export function formValuesToSaveInput(data: ProductVariantsFormValues): SaveAddOnsInput {
  return {
    hideVariantFromProfile: !data.showVariants,
    addOns: data.variants.map((variant) => ({
      ...(variant.variant_id ? {id: variant.variant_id} : {}),
      name: variant.name,
      options: variant.options.map((option) => ({
        ...(option.variant_option_id ? {id: option.variant_option_id} : {}),
        name: option.name,
        // A free option is sent as zero-amount rows for its currencies, NOT an
        // empty array — the API rejects empty `currencies` ("should not be
        // empty"). The currency codes come from the form (the price inputs are
        // just hidden while the option is free). Paid options send the entered
        // prices.
        currencies: option.currencies.map((currency) => ({
          currency: currency.currency,
          amount: option.priceType === "free" ? 0 : Number(currency.price),
          isManuallyAdded: true,
        })),
      })),
    })),
  };
}

/**
 * Diff a loaded snapshot against the submitted form to find what the coach
 * removed. Add-on groups and options are only deleted explicitly (the PUT
 * upsert never removes omitted elements). A removed group covers its own
 * options, so options are only collected for surviving groups.
 */
export function computeAddOnDeletions(
  snapshot: AddOn[],
  data: ProductVariantsFormValues
): {removedAddOnIds: string[]; removedOptions: {addOnId: string; optionId: string}[]} {
  const submittedAddOnIds = new Set(
    data.variants.map((v) => v.variant_id).filter(Boolean) as string[]
  );

  const removedAddOnIds: string[] = [];
  const removedOptions: {addOnId: string; optionId: string}[] = [];

  snapshot.forEach((addOn) => {
    if (!addOn.id) return;
    if (!submittedAddOnIds.has(addOn.id)) {
      removedAddOnIds.push(addOn.id);
      return;
    }
    const submittedGroup = data.variants.find((v) => v.variant_id === addOn.id);
    const submittedOptionIds = new Set(
      (submittedGroup?.options.map((o) => o.variant_option_id).filter(Boolean) ??
        []) as string[]
    );
    addOn.options.forEach((option) => {
      if (option.id && !submittedOptionIds.has(option.id)) {
        removedOptions.push({addOnId: addOn.id!, optionId: option.id});
      }
    });
  });

  return {removedAddOnIds, removedOptions};
}
